import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Typography,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/ErrorOutline";
import theme from "../../theme";
import CommonButton from "../button";

// Spec's validation_status buckets — GREEN safe, AMBER discrepancy, RED no
// HR match, PENDING not run yet. The API sometimes returns older-style buckets
// (APPROVED / FAILED / SUBMITTED), so we normalize both to the same styling
// slot before deciding whether to show the warning strip.
const VALIDATION_META = {
  GREEN: { label: "Green", tone: "ok", cue: null },
  APPROVED: { label: "Green", tone: "ok", cue: null },
  AMBER: {
    label: "Amber",
    tone: "warn",
    cue: "Validation found discrepancies. Review the audit log before approving.",
  },
  RED: {
    label: "Red",
    tone: "bad",
    cue: "No HR master match. Confirm this is a legitimate employee before approving.",
  },
  FAILED: {
    label: "Red",
    tone: "bad",
    cue: "Validation failed. Confirm this is a legitimate employee before approving.",
  },
  PENDING: {
    label: "Pending",
    tone: "info",
    cue: "Validation hasn't run yet — you're approving before the automated checks complete.",
  },
  SUBMITTED: {
    label: "Pending",
    tone: "info",
    cue: "Validation hasn't run yet — you're approving before the automated checks complete.",
  },
};

const useStyles = makeStyles(() => ({
  paper: { borderRadius: 14, width: "100%", maxWidth: 460 },
  title: {
    padding: "18px 22px 8px",
    "& h2": {
      margin: 0,
      fontFamily: theme.fontFamily.MARKPROBOLD,
      color: theme.colors.Violet,
      fontSize: 17,
    },
  },
  subtitle: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    fontSize: 12.5,
    marginTop: 2,
  },
  content: { padding: "8px 22px 4px", display: "flex", flexDirection: "column", gap: 14 },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: "10px 12px",
    background: theme.colors.Porcelain,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: 10,
  },
  metaLabel: {
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    color: "#8892a4",
  },
  chipOk: {
    background: "#E8FDF1",
    color: "#1A8C4E",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11.5,
    height: 24,
  },
  chipWarn: {
    background: "#FEF3C7",
    color: "#B45309",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11.5,
    height: 24,
  },
  chipBad: {
    background: theme.colors.Cinderella,
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11.5,
    height: 24,
  },
  chipInfo: {
    background: "#ECF3FF",
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 11.5,
    height: 24,
  },
  cueStrip: {
    display: "flex",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12.5,
    lineHeight: 1.45,
  },
  cueWarn: {
    background: "#FEF3C7",
    color: "#78350F",
    border: "1px solid #FDE68A",
  },
  cueBad: {
    background: theme.colors.Cinderella,
    color: "#7F1D1D",
    border: "1px solid #F7C6CD",
  },
  cueInfo: {
    background: "#ECF3FF",
    color: theme.colors.Violet,
    border: `1px solid ${theme.colors.Periwinkle}`,
  },
  actions: { padding: "12px 22px 18px", gap: 10 },
}));

const chipClassFor = (tone, classes) => {
  if (tone === "ok") return classes.chipOk;
  if (tone === "warn") return classes.chipWarn;
  if (tone === "bad") return classes.chipBad;
  return classes.chipInfo;
};

const cueClassFor = (tone, classes) => {
  if (tone === "warn") return `${classes.cueStrip} ${classes.cueWarn}`;
  if (tone === "bad") return `${classes.cueStrip} ${classes.cueBad}`;
  return `${classes.cueStrip} ${classes.cueInfo}`;
};

export default function ApproveConfirmModal({
  open,
  employeeName,
  validationStatus,
  onCancel,
  onConfirm,
  loading = false,
}) {
  const classes = useStyles();
  const meta = VALIDATION_META[validationStatus] || {
    label: validationStatus || "Unknown",
    tone: "info",
    cue: null,
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      classes={{ paper: classes.paper }}
      aria-labelledby="approve-join-request-title"
    >
      <DialogTitle
        id="approve-join-request-title"
        className={classes.title}
        disableTypography
      >
        <h2>Approve this join request?</h2>
        <Typography className={classes.subtitle}>
          {employeeName
            ? `${employeeName} will be provisioned as an active employee.`
            : "The employee will be provisioned as active."}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <div className={classes.metaRow}>
          <span className={classes.metaLabel}>Validation</span>
          <Chip
            size="small"
            label={meta.label}
            className={chipClassFor(meta.tone, classes)}
          />
        </div>

        {meta.cue ? (
          <div className={cueClassFor(meta.tone, classes)}>
            <WarningIcon fontSize="small" style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{meta.cue}</span>
          </div>
        ) : null}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <CommonButton
          type="button"
          text="Cancel"
          stylevariant="secondary2"
          onClick={onCancel}
          disabled={loading}
          style={{ padding: "8px 20px" }}
        />
        <CommonButton
          type="button"
          text={loading ? "Approving…" : "Approve"}
          stylevariant="primary"
          onClick={onConfirm}
          disabled={loading}
          style={{ padding: "8px 22px" }}
        />
      </DialogActions>
    </Dialog>
  );
}
